/**
 * The learner's light/dark choice — the ONE appearance mechanism the LV has,
 * extracted from LvShell so the masthead's Appearance menu and the sign-in
 * gate share it (previously the toggle lived inline in LvShell, mirrored to
 * the assistant bar). The deployment's brand.nightMode policy (an lv_settings
 * brand row, authored in capcom's Branding dialog) decides whether a choice
 * exists at all and which side it defaults to:
 *
 *   off     → light only; no choice is offered anywhere
 *   toggle  → learner may choose Light or Dark; light default
 *   dark    → learner may choose Light or Dark; dark default
 *
 * The choice itself stays browser-session state (sessionStorage lv:mode) —
 * never a server write, never a new store. There is deliberately no System
 * option and no learner accent: both belong to the Learner View Branding
 * model in capcom, not to the harness.
 */
import { useState } from 'react'
import { brandNightMode, nightModeAllowed, type LvClientConfig } from './lvConfig'

const MODE_KEY = 'lv:mode'

type Choice = 'light' | 'dark' | null

function storedChoice(): Choice {
  try {
    const v = sessionStorage.getItem(MODE_KEY)
    return v === 'dark' || v === 'light' ? v : null
  } catch { return null }
}

export function useBrandMode(cfg: LvClientConfig | null): {
  /** The mode to build the theme with, policy applied. */
  mode: 'light' | 'dark'
  /** Whether the learner gets a choice at all (the brand.nightMode policy). */
  allowed: boolean
  /** Record an explicit choice (sessionStorage lv:mode, same as always). */
  choose: (m: 'light' | 'dark') => void
} {
  const [choice, setChoice] = useState<Choice>(storedChoice)
  const policy = cfg ? brandNightMode(cfg) : undefined
  const mode: 'light' | 'dark' =
    policy === 'off' ? 'light' : (choice ?? (policy === 'dark' ? 'dark' : 'light'))
  function choose(m: 'light' | 'dark') {
    setChoice(m)
    try { sessionStorage.setItem(MODE_KEY, m) } catch { /* private mode */ }
  }
  return { mode, allowed: nightModeAllowed(cfg), choose }
}
